import { DeleteOutlined, EnvironmentOutlined, KeyOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Divider,
  Empty,
  Flex,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
} from 'antd';
import { useIntl } from 'react-intl';
import { LOCATION_CREDENTIAL_SOURCES, type StartupSessionSnapshot } from '@/app/session/startup-session';
import { messages } from './messages';

type StartupLocation = StartupSessionSnapshot['locations'][number];

interface StartupLocationStepProps {
  activeLocation: StartupLocation | null;
  apiKey: string;
  isAddLocationOpen: boolean;
  isValidatingLocation: boolean;
  locationApiKeyError: string | null;
  locations: StartupLocation[];
  onAddLocationCancel: () => void;
  onAddLocationOpen: () => void;
  onApiKeyChange: (apiKey: string) => void;
  onApiKeySubmit: () => void;
  onChangeEmail: () => void;
  onContinue: () => void;
  onRemoveLocation: (locationId: string) => void;
  onSelectLocation: (locationId: string) => void;
  selectedLocationId: string | null;
}

export function StartupLocationStep({
  activeLocation,
  apiKey,
  isAddLocationOpen,
  isValidatingLocation,
  locationApiKeyError,
  locations,
  onAddLocationCancel,
  onAddLocationOpen,
  onApiKeyChange,
  onApiKeySubmit,
  onChangeEmail,
  onContinue,
  onRemoveLocation,
  onSelectLocation,
  selectedLocationId,
}: StartupLocationStepProps) {
  const intl = useIntl();
  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Button block icon={<KeyOutlined />} onClick={onAddLocationOpen} size="large">
        {intl.formatMessage(messages.startupEnterLocationApiKey)}
      </Button>

      {activeLocation ? (
        <SelectedLocationCard
          isValidatingLocation={isValidatingLocation}
          location={activeLocation}
          onContinue={onContinue}
          onRemove={onRemoveLocation}
        />
      ) : (
        <Empty
          description={intl.formatMessage(messages.startupEnterLocationApiKeyToContinue)}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {locations.length > 1 ? (
        <List
          bordered
          dataSource={locations.filter(location => location.id !== selectedLocationId)}
          header={intl.formatMessage(messages.startupOtherSavedLocations)}
          renderItem={location => (
            <List.Item
              actions={[
                <Button key="use" onClick={() => onSelectLocation(location.id)} type="link">
                  {intl.formatMessage(messages.startupUseLocation)}
                </Button>,
                <RemoveLocationButton key="remove" location={location} onRemove={onRemoveLocation} />,
              ]}
            >
              <List.Item.Meta avatar={<EnvironmentOutlined />} title={location.locationName} />
            </List.Item>
          )}
        />
      ) : null}

      <Divider />

      <Flex gap="small" justify="flex-start" wrap>
        <Button onClick={onChangeEmail} size="large">
          {intl.formatMessage(messages.startupChangeEmail)}
        </Button>
      </Flex>

      <LocationApiKeyModal
        apiKey={apiKey}
        error={locationApiKeyError}
        isOpen={isAddLocationOpen}
        isValidating={isValidatingLocation}
        onCancel={onAddLocationCancel}
        onChange={onApiKeyChange}
        onSubmit={onApiKeySubmit}
      />
    </Space>
  );
}

function SelectedLocationCard({
  isValidatingLocation,
  location,
  onContinue,
  onRemove,
}: {
  isValidatingLocation: boolean;
  location: StartupLocation;
  onContinue: () => void;
  onRemove: (locationId: string) => void;
}) {
  const intl = useIntl();
  return (
    <Card
      size="small"
      style={{
        borderColor: location.source === LOCATION_CREDENTIAL_SOURCES.url ? '#1677ff' : undefined,
        background: location.source === LOCATION_CREDENTIAL_SOURCES.url ? '#f0f7ff' : undefined,
      }}
    >
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <Flex align="center" gap="small" justify="space-between">
          <Typography.Text ellipsis strong style={{ minWidth: 0 }}>
            <EnvironmentOutlined /> {location.locationName}
          </Typography.Text>
          <Space size={4}>
            {location.source === LOCATION_CREDENTIAL_SOURCES.url ? (
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {intl.formatMessage(messages.startupFromLink)}
              </Tag>
            ) : null}
            <RemoveLocationButton location={location} onRemove={onRemove} />
          </Space>
        </Flex>
        <Button block disabled={isValidatingLocation} onClick={onContinue} size="large" type="primary">
          {intl.formatMessage(messages.startupContinueWithLocation)}
        </Button>
      </Space>
    </Card>
  );
}

function RemoveLocationButton({
  location,
  onRemove,
}: {
  location: StartupLocation;
  onRemove: (locationId: string) => void;
}) {
  const intl = useIntl();

  return (
    <Popconfirm
      okButtonProps={{ danger: true }}
      okText={intl.formatMessage(messages.startupRemoveLocation)}
      onConfirm={() => onRemove(location.id)}
      title={intl.formatMessage(messages.startupRemoveLocationConfirm, { location: location.locationName })}
    >
      <Button danger icon={<DeleteOutlined />} type="text">
        {intl.formatMessage(messages.startupRemoveLocation)}
      </Button>
    </Popconfirm>
  );
}

function LocationApiKeyModal({
  apiKey,
  error,
  isOpen,
  isValidating,
  onCancel,
  onChange,
  onSubmit,
}: {
  apiKey: string;
  error: string | null;
  isOpen: boolean;
  isValidating: boolean;
  onCancel: () => void;
  onChange: (apiKey: string) => void;
  onSubmit: () => void;
}) {
  const intl = useIntl();
  return (
    <Modal
      destroyOnHidden
      footer={null}
      onCancel={onCancel}
      open={isOpen}
      title={intl.formatMessage(messages.startupEnterLocationApiKey)}
    >
      <Form layout="vertical" onFinish={onSubmit}>
        {error ? <Alert message={error} showIcon style={{ marginBottom: 16 }} type="error" /> : null}
        <Form.Item label={intl.formatMessage(messages.appAuthPageApiKey)}>
          <Input
            autoComplete="off"
            autoFocus
            onChange={event => onChange(event.target.value)}
            placeholder={intl.formatMessage(messages.appAuthPageApiKeyPlaceholder)}
            prefix={<KeyOutlined />}
            size="large"
            value={apiKey}
          />
        </Form.Item>
        <Flex gap="small" justify="space-between" wrap>
          <Button onClick={onCancel} size="large">
            {intl.formatMessage(messages.appCommonCancel)}
          </Button>
          <Button
            disabled={!apiKey.trim() || isValidating}
            htmlType="submit"
            loading={isValidating}
            size="large"
            type="primary"
          >
            {intl.formatMessage(messages.startupAddLocation)}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
}
